import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageEditMode } from '@/context/PageEditModeContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Click-to-edit for a single field on a database-backed class record.
 *
 * Renders plain text when the page is NOT in edit mode. When the admin
 * clicks "Edit page" (usePageEditMode -> enabled=true), the field becomes
 * an input/textarea/select. On blur, it PUTs the change to
 * PUT /api/admin/classes/class/{classId}.
 */
const InlineClassField = ({
  classId,
  field,
  value,
  as: Wrapper = 'span',
  className = '',
  multiline = false,
  type = 'text',
  options,
  prefix = '',
  suffix = '',
  onSaved,
  inputClassName = '',
  placeholder = '',
}) => {
  const { enabled } = usePageEditMode();
  const [local, setLocal] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocal(value ?? '');
  }, [value]);

  const save = async () => {
    const nextValue = type === 'number' ? Number(local) : local;
    const currentValue = type === 'number' ? Number(value ?? 0) : (value ?? '');
    if (nextValue === currentValue) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/classes/class/${classId}`, { [field]: nextValue });
      toast.success('Saved');
      onSaved?.(nextValue);
    } catch (err) {
      toast.error('Save failed');
      setLocal(value ?? '');
    } finally {
      setSaving(false);
    }
  };

  if (!enabled) {
    // When not editing, prefer the human label if options are provided.
    const displayValue = options
      ? (options.find((o) => o.value === value)?.label ?? value)
      : value;
    return (
      <Wrapper className={className}>
        {prefix}{displayValue}{suffix}
      </Wrapper>
    );
  }

  const base =
    'bg-black/40 border border-yellow-500/60 rounded px-2 py-1 focus:border-yellow-400 focus:outline-none text-inherit font-inherit';

  if (options) {
    return (
      <select
        className={`${base} ${inputClassName}`}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={save}
        disabled={saving}
        data-testid={`inline-class-${field}-${classId}`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  if (multiline) {
    return (
      <textarea
        className={`${base} w-full ${inputClassName}`}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={save}
        disabled={saving}
        rows={2}
        placeholder={placeholder}
        data-testid={`inline-class-${field}-${classId}`}
      />
    );
  }

  return (
    <input
      type={type === 'number' ? 'number' : 'text'}
      className={`${base} ${inputClassName}`}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={save}
      disabled={saving}
      placeholder={placeholder}
      data-testid={`inline-class-${field}-${classId}`}
    />
  );
};

export default InlineClassField;
